import './AutoCompleteTreeFilter.scss';
import { MultiSelect, MultiSelectChangeEvent, TagData } from '@progress/kendo-react-dropdowns';
import { useAppSelector } from 'common/hooks/redux';
import useClaims from 'common/hooks/useClaims';
import {
  AutocompleteFilterModel,
  CommonFilterObject,
  CommonFiltersPayloadAction,
  DatasetID,
  FilterType,
  FilterValueObject,
  CommonFilterList,
} from 'common/models/filter.model';
import { Claim } from 'common/models/page-config.models';
import { RootState } from 'common/store';
import { FilterResponse, useGetFilterValuesQuery } from 'common/store/api/filters.api';
import {
  addCommonFilters,
  removeCommonFilter,
  removeCommonFilters,
  selectSurveyFilters,
} from 'common/store/slices/common-filters.slice';
import { selectDatasetQuestion } from 'common/store/slices/dataset.slice';
import {
  selectTempEndDate,
  selectTempStartDate,
  selectTempVisualData,
  selectTempVisualType,
} from 'common/store/slices/date-filter.slice';
import { selectFilterParent } from 'common/store/slices/filter-parent.slice';
import GroupOptions, { DataItem } from './GroupData/GroupOptions';
import { useDispatch } from 'react-redux';
import { formatElementId } from '../widgets/utils';
import useFilterValidation from 'common/hooks/useFilterValidation';
import { DEFAULT_END_DATE, DEFAULT_FILTER_VALIDATION_MESSAGE } from 'common/constants';
import { cloneElement, useMemo, useRef } from 'react';
import { adjustFilterDates } from 'common/hooks/utils';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { useParams } from 'react-router-dom';

function findMatchingItem(data: FilterResponse[], target: FilterValueObject): DataItem | null {
  // Function to search recursively in nested structures
  function search(items: FilterResponse[], parents: DataItem[] = []): DataItem | null {
    for (const item of items) {
      // Check if the current item matches both name and value
      if (item.name === target.name && item.value === target.value) {
        return { ...(item as DataItem), parents }; // Return the matching item with parents
      }

      // If there are nested items, search recursively
      if (item.items && item.items.length > 0) {
        const found: FilterResponse | null = search(item.items, [...parents, item as DataItem]);
        if (found) {
          return found as DataItem; // Return the found item from the nested search
        }
      }
    }
    return null; // No match found
  }

  return search(data);
}

const convertDItemToFilterItem = (item: DataItem): FilterValueObject => {
  return {
    name: item.name,
    value: item.value,
  };
};

export interface HandleCheckBoxChange {
  item: DataItem;
  parents: DataItem[];
  children: DataItem[];
  value: boolean;
}

function AutoCompleteTreeFilter({
  label,
  property,
  datasetId,
  overrideParentFilter,
  requiredFilters,
  validationMessage,
  toolTip,
}: Omit<AutocompleteFilterModel, 'options'> & DatasetID) {
  const { hasClaim } = useClaims();
  const dispatch = useDispatch();

  const popupContainerRef = useRef(null);
  const tempStartDate = useAppSelector((state: RootState) => selectTempStartDate(state, datasetId));
  const visualData = useAppSelector((state: RootState) => selectTempVisualData(state, datasetId));
  const visualType = useAppSelector((state: RootState) => selectTempVisualType(state, datasetId));
  const tempEndDate =
    useAppSelector((state: RootState) => selectTempEndDate(state, datasetId)) ?? DEFAULT_END_DATE;
  const { projectId } = useParams();
  const question = useAppSelector((state: RootState) =>
    selectDatasetQuestion(state, datasetId, projectId ?? '')
  );
  const filterParent = useAppSelector(selectFilterParent);
  const { activeProject } = useModuleProjectContext();
  const selectedFilters = useAppSelector((state: RootState) =>
    selectSurveyFilters(state, datasetId)
  );
  const required: boolean = requiredFilters ? requiredFilters.length > 0 : false;
  const filterValid = useFilterValidation({ datasetId, filters: requiredFilters });

  const { startDate, endDate } = adjustFilterDates({
    startDate: tempStartDate,
    endDate: tempEndDate,
    visualData,
    visualType,
  });

  const { data, isLoading, isFetching } = useGetFilterValuesQuery(
    {
      filterString: property,
      startDate,
      endDate,
      questionId: question?.id ?? -1,
      filterParent: overrideParentFilter ?? filterParent,
      includeAlignment: hasClaim(Claim.Alignment),
    },
    {
      skip: !activeProject,
      refetchOnMountOrArgChange: true,
    }
  );

  // Get the selected categories, questions, and responses
  const groupedSelectedValues: { [key: string]: DataItem[] } = useMemo(() => {
    const categories =
      data?.filter((item) =>
        selectedFilters.find(
          (filterItem) => filterItem.name === item.name && filterItem.value === item.value
        )
      ) ?? [];

    const questions = categories.reduce((questions: FilterResponse[], category) => {
      const categoryQuestions = category.items?.filter((item) => {
        return selectedFilters.find(
          (filterItem) => filterItem.name === item.name && filterItem.value === item.value
        );
      });

      if (categoryQuestions) {
        questions.push(...categoryQuestions.map((item) => ({ ...item, parents: [category] })));
      }

      return questions;
    }, []);

    const responses = questions.reduce((responses: FilterResponse[], question) => {
      const questionResponses = question.items?.filter((item) => {
        return selectedFilters.find(
          (filterItem) => filterItem.name === item.name && filterItem.value === item.value
        );
      });

      if (questionResponses) {
        responses.push(...questionResponses.map((item) => ({ ...item, parents: [question] })));
      }

      return responses;
    }, []);

    return {
      selectedCategories: categories as DataItem[],
      selectedQuestions: questions as DataItem[],
      selectedResponses: responses as DataItem[],
    };
  }, [selectedFilters, data]);

  const { selectedCategories, selectedQuestions, selectedResponses } = groupedSelectedValues;

  const groupedSelectedFilters = [
    ...(selectedCategories.length > 0 ? [{ name: '_Categories' }, ...selectedCategories] : []),
    ...(selectedQuestions.length > 0 ? [{ name: '_Questions' }, ...selectedQuestions] : []),
    ...(selectedResponses.length > 0 ? [{ name: '_Responses' }, ...selectedResponses] : []),
  ];

  const updateFilters = (additionalFilters: DataItem[], value: boolean) => {
    const combinedItems = additionalFilters.reduce(
      (items: { [key: string]: CommonFilterObject[] }, parent) => {
        if (items[parent.filterType]) {
          items[parent.filterType].push(convertDItemToFilterItem(parent));
        } else {
          items[parent.filterType] = [convertDItemToFilterItem(parent)];
        }

        return items;
      },
      {}
    );

    const filterItems: CommonFiltersPayloadAction[] = Object.keys(combinedItems).map((key) => {
      const getLabel = () => {
        if (key === (CommonFilterList.Response as string)) {
          return 'Response';
        }
        if (key === (CommonFilterList.Survey as string)) {
          return 'Category';
        }
        return key;
      };
      return {
        type: FilterType.AUTOCOMPLETE,
        key,
        label: getLabel(),
        filters: combinedItems[key],
      };
    });

    if (value) {
      dispatch(addCommonFilters({ datasetId, filters: filterItems }));
    } else {
      dispatch(removeCommonFilters({ datasetId, filters: filterItems }));
    }
  };

  const handleCheckBoxChange = (clickData: HandleCheckBoxChange): void => {
    const { item, value, parents } = clickData;

    // If the item is being un-checked
    if (!value) {
      // Calculate the selected children items
      const selectedChildren =
        item?.items?.reduce((children: DataItem[], item: DataItem) => {
          if (selectedFilters.find((val) => val.value === item.value)) {
            children.push(item);
          }

          item?.items?.forEach((child) => {
            if (selectedFilters.find((val) => val.value === child.value)) {
              children.push(child);
            }
          });

          return children;
        }, []) ?? [];

      if (required && item.filterType === (CommonFilterList.Question as string)) {
        // If field is required, and item is a question, remove parent category and ALL children
        return updateFilters([...parents, item, ...selectedChildren], value);
      } else {
        // Otherwise, just remove the item and it's children
        return updateFilters([...selectedChildren, item], value);
      }
    }

    if (required && item.filterType === (CommonFilterList.Question as string)) {
      // If field is required, and item is a question, remove all other categories, questions and responses
      if (selectedQuestions.length > 0) {
        const parents = selectedQuestions[0]?.parents ?? [];
        const children = selectedQuestions[0]?.items ?? [];
        updateFilters([...selectedQuestions, ...parents, ...children], false);
      }

      // Add the selected Question and it's Parent category
      return updateFilters([...parents, item], value);
    } else {
      // Otherwise, just add the item and it's children
      return updateFilters([...parents, item], value);
    }
  };

  const handleMultiSelectOnChange = (event: MultiSelectChangeEvent) => {
    const { value } = event;

    // If value is empty, remove all filters
    if (value.length === 0) {
      dispatch(removeCommonFilter({ datasetId, filterKey: CommonFilterList.Survey as string }));
      dispatch(removeCommonFilter({ datasetId, filterKey: CommonFilterList.Question as string }));
      dispatch(removeCommonFilter({ datasetId, filterKey: CommonFilterList.Response as string }));
      return;
    }

    const selectedItem = selectedFilters.find(
      (filterItem) => !value.find((valueItem: string) => valueItem === filterItem.name)
    );

    const selectedDataItem = findMatchingItem(
      data as FilterResponse[],
      selectedItem as FilterValueObject
    );

    if (selectedDataItem) {
      handleCheckBoxChange({
        item: selectedDataItem,
        parents: selectedDataItem.parents ?? [],
        children: [],
        value: false,
      });
    }
  };

  const tagRender = (
    tagData: TagData,
    li: React.ReactElement<HTMLLIElement, string | React.JSXElementConstructor<any>>
  ) =>
    cloneElement(li, li.props, [
      (
        <span className={`cqr-tag ${tagData.text.startsWith('_') ? 'title' : ''}`}>
          {tagData.text.startsWith('_') ? tagData.text.replace('_', '') : tagData.text}
        </span>
      ) as any,
      li.props.children,
    ]);
  return (
    <div
      ref={popupContainerRef}
      className={`form-row autocomplete-filter ${!filterValid ? 'invalid' : ''}`}
    >
      <MultiSelect
        popupSettings={{
          appendTo: popupContainerRef.current,
          positionMode: 'absolute',
          offset: { left: 0, top: 0 },
          popupClass: 'autocomplete-popup',
        }}
        className={`auto-complete-tree-filter ${groupedSelectedFilters.length > 0 ? 'values' : ''}`}
        required={required}
        id={`filter-${formatElementId(label)}`}
        loading={isLoading || isFetching}
        label={label}
        data={data}
        value={groupedSelectedFilters.map((val) => val.name)}
        onChange={handleMultiSelectOnChange}
        autoClose={false}
        itemRender={(_, props) => {
          return (
            <GroupOptions
              required={required}
              selectedGroups={groupedSelectedValues}
              selectedFilters={selectedFilters}
              parents={[]}
              dataItem={props.dataItem}
              handleCheckBoxChange={handleCheckBoxChange}
              toolTip={toolTip}
            />
          );
        }}
        tagRender={tagRender}
      />
      {!filterValid && (
        <div className="invalid-feedback">
          {validationMessage ?? DEFAULT_FILTER_VALIDATION_MESSAGE}
        </div>
      )}
    </div>
  );
}

export default AutoCompleteTreeFilter;
